import React, { useEffect, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ThemeProvider, alpha } from "@mui/material/styles";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";

import MenuIcon from "@mui/icons-material/Menu";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";

import { AuthContext } from "../../context/AuthContext";
import { ConfiguracionContext } from "../../context/ConfiguracionContext";
import { getBrand, createBrandTheme } from "../../theme/brand";
import "../../assets/css/BrandPrimereact.css";
import AppMenu from "./AppMenu";

function Barra() {
  const navigate = useNavigate();

  const authContext = useContext(AuthContext);
  const { state, cerrarSesion, usuarioAutenticado } = authContext;

  const configuracionContext = useContext(ConfiguracionContext);
  const { configuracion, obtenerConfiguracion } = configuracionContext;

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [fechaTope, setFechaTope] = useState(null);
  const [disableMenu, setDisableMenu] = useState(false);

  useEffect(() => {
    obtenerConfiguracion();
    usuarioAutenticado();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Bloqueo de menú por vencimiento de versión (destinatario 5).
  useEffect(() => {
    if (configuracion && configuracion.destinatario === 5 && !fechaTope) {
      const [dia, anioCorto, mes] = configuracion.versionWeb.split(".");
      const tope = new Date(`202${anioCorto}`, mes - 1, dia);
      if (Date.now() > tope.getTime()) setDisableMenu(true);
      setFechaTope(tope);
    }
  }, [configuracion, fechaTope]);

  const brand = getBrand(configuracion);
  const theme = createBrandTheme(brand);

  // Expone el color de la marca activa como variable CSS global, para que los
  // componentes de PrimeReact (Despachos, Tripulaciones, etc.) usen el color
  // del destinatario seleccionado en lugar del azul fijo de saga-blue.
  // Mientras no haya destinatario resuelto se deja el fallback del CSS.
  useEffect(() => {
    const root = document.documentElement;
    if (brand && brand.key !== "default") {
      root.style.setProperty("--brand-color", brand.color);
    } else {
      root.style.removeProperty("--brand-color");
    }
  }, [brand.key, brand.color]);

  const autenticado = state && state.autenticado === true;
  const mostrarMenu = autenticado && !disableMenu;

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const cerrar = () => {
    handleMenuClose();
    cerrarSesion();
    navigate("/");
    setTimeout(() => window.location.reload(), 1200);
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: "flex", mb: { xs: "56px", sm: "64px" } }}>
        <AppBar
          position="fixed"
          elevation={0}
          color="primary"
          sx={{
            borderBottom: (t) => `1px solid ${alpha("#000", 0.12)}`,
            background: (t) =>
              `linear-gradient(180deg, ${t.palette.primary.main} 0%, ${alpha(
                t.palette.primary.main,
                0.92
              )} 100%)`,
          }}
        >
          <Toolbar sx={{ gap: 1 }}>
            {mostrarMenu ? (
              <Tooltip title="Menú">
                <IconButton
                  edge="start"
                  color="inherit"
                  aria-label="abrir menú"
                  onClick={() => setDrawerOpen(true)}
                >
                  <MenuIcon />
                </IconButton>
              </Tooltip>
            ) : null}

            <Box
              component="a"
              href="/"
              sx={{ display: "inline-flex", alignItems: "center", ml: mostrarMenu ? 0.5 : 0 }}
            >
              <Box
                component="img"
                src={brand.logo}
                alt={brand.label}
                sx={{ height: brand.logoHeight, width: "auto", display: "block" }}
              />
            </Box>

            <Box sx={{ flexGrow: 1 }} />

            {autenticado ? (
              <Typography
                variant="body2"
                sx={{ display: { xs: "none", sm: "block" }, fontWeight: 600, opacity: 0.95 }}
              >
                {state.nombre}
              </Typography>
            ) : null}

            <Tooltip title="Cuenta">
              <IconButton
                edge="end"
                color="inherit"
                aria-label="cuenta de usuario"
                onClick={handleMenuOpen}
                sx={{ display: { xs: "none", sm: "inline-flex" } }}
              >
                <AccountCircleIcon />
              </IconButton>
            </Tooltip>

            <IconButton
              edge="end"
              color="inherit"
              aria-label="más opciones"
              onClick={handleMenuOpen}
              sx={{ display: { xs: "inline-flex", sm: "none" } }}
            >
              <MoreVertIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          {!autenticado ? (
            <MenuItem
              onClick={() => {
                handleMenuClose();
                navigate("/login");
              }}
            >
              <LoginIcon fontSize="small" style={{ marginRight: 8 }} />
              Iniciar Sesión
            </MenuItem>
          ) : (
            <MenuItem onClick={cerrar}>
              <LogoutIcon fontSize="small" style={{ marginRight: 8 }} />
              Cerrar Sesión
            </MenuItem>
          )}
        </Menu>

        {mostrarMenu ? (
          <AppMenu
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            configuracion={configuracion}
          />
        ) : null}
      </Box>
    </ThemeProvider>
  );
}

export default Barra;
